import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import { PHOTOS } from "@/lib/photos";

const HERO_SHOT = PHOTOS.soundbathHero;

const BULLETS = [
  { icon: "🎹", title: "Live, improvised piano", body: "The music you're hearing has never been played exactly this way before, and never will be again." },
  { icon: "✡️", title: "Rooted in Jewish musical tradition", body: "Motifs, modes, and rhythms drawn from liturgy and folk sources, instead of the usual crystal-bowl-and-gong template." },
  { icon: "🕯️", title: "Emotionally layered", body: "Calm, yes. But also textured, expressive, and unexpected, jazz and classical methodologies woven through." },
  { icon: "🧠", title: "Culturally resonant", body: "Built for communities that want a soundbath that actually sounds like home." },
  { icon: "🛋️", title: "Accessible to anyone", body: "No prior experience with meditation or music. Just sit back and let the sound wash over you." },
];

const PERFECT_FOR = [
  { icon: "🌒", label: "Pre-holiday programming" },
  { icon: "📚", label: "Adult education or cultural learning" },
  { icon: "🎭", label: "Interdisciplinary art experiences" },
  { icon: "🌿", label: "Retreats and reflective gatherings" },
  { icon: "🏠", label: "Private home events (minimum group size)" },
];

const TESTIMONIALS = [
  { quote: "Doniel beautifully wove together classic Jewish tunes with his own musical interpretation. Going to the soundbath was a spiritual experience through familiar niggunim combined with his own talent.", title: "a spiritual experience", who: "Xany Z.", photo: PHOTOS.warmRoom },
  { quote: "As a mom of little ones, I miss the comfort and poignancy of the familiar niggunim. This soundbath was an incredible opportunity to tap into the power of those melodies. It felt like the music spoke straight to my neshama.", title: "spoke straight to my neshama", who: "Nora R.", photo: PHOTOS.quietRoom },
  { quote: "Listening to Dan playing piano is simultaneously calming, uplifting and entertaining. As I listened to him skillfully transform classic niggunim into jazzified pieces, I was able to completely relax and let my body and soul be washed by the spiritual and beautiful notes.", title: "I was able to completely relax", who: "Ruth B.", photo: PHOTOS.danPiano },
];

export default function Soundbaths() {
  return (
    <MarketingShell active="/soundbaths">
      <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Piano soundbaths
        </p>
        <h1
          className="leading-[0.95]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: "clamp(40px, 5.6vw, 84px)", textWrap: "balance", maxWidth: "20ch" }}
        >
          An hour of <em style={{ color: "#F2D589" }}>nothing to do</em> but listen.
        </h1>
        <p className="mt-5 max-w-[46rem] text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
          Immersive, improvised solo piano for deep listening and cultural
          resonance. No setlist. No stage. No clapping. Just music
          unfolding in real time while you sit (or lie down, honestly,
          please lie down) and receive it.
        </p>
      </motion.section>

      <section className="mt-12 rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
        <img src={HERO_SHOT} alt="A quiet sound space" className="w-full max-h-[520px] object-cover" />
      </section>

      <section className="mt-16">
        <h2 className="text-3xl sm:text-4xl mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          What <em>is</em> a piano soundbath, exactly?
        </h2>
        <div className="space-y-4 text-base sm:text-lg leading-relaxed max-w-[58rem]" style={{ color: "rgba(245,239,227,0.82)" }}>
          <p>
            A live, uninterrupted listening experience. Not a concert.
            No stage. No clapping. Music unfolding in real time while
            you&apos;re invited to sit or lie down, close your eyes,
            and just receive the sound.
          </p>
          <p>
            Where most soundbaths use crystal bowls or gongs rooted in
            Eastern tradition, this one&apos;s different: solo piano,
            drawing from Jewish modes, <em>nigunim</em>, and folk
            melodies. Improvised, but grounded in structure. Spacious,
            but emotionally alive.
          </p>
          <p>
            It&apos;s an experience of sound <em>without</em>{" "}
            distraction, a chance to slow down, reconnect, and
            hear something you didn&apos;t know you needed.
          </p>
        </div>
      </section>

      <section className="mt-16">
        <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          What makes this different
        </h2>
        <ul className="grid sm:grid-cols-2 gap-5">
          {BULLETS.map((b) => (
            <li
              key={b.title}
              className="rounded-2xl border p-5 backdrop-blur-md"
              style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,25,0.4)" }}
            >
              <p className="text-2xl mb-1" aria-hidden="true">{b.icon}</p>
              <p className="text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {b.title}
              </p>
              <p className="text-sm leading-relaxed" style={{ color: "rgba(245,239,227,0.7)" }}>
                {b.body}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-16">
        <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          What to expect
        </h2>
        <p className="text-base sm:text-lg leading-relaxed max-w-[58rem] mb-4" style={{ color: "rgba(245,239,227,0.82)" }}>
          A soundbath is less about watching and more about{" "}
          <em>receiving</em>. The room stays intentionally quiet, no formal stage, no audience interaction. The music takes
          center space and unfolds without interruption.
        </p>
        <ul className="space-y-1 text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.78)" }}>
          <li>&middot; 45&ndash;60 minutes of uninterrupted solo piano</li>
          <li>&middot; A quiet space where attendees can lie down or sit comfortably</li>
          <li>&middot; Optional short intro (5 minutes) to ground the group</li>
          <li>&middot; No talking, phones, or applause, just deep listening</li>
          <li>&middot; A few moments of silence at the end before re-entry</li>
        </ul>
      </section>

      <section className="mt-16">
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          From people like you
        </p>
        <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Audience feedback
        </h2>
        <ul className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <li
              key={t.who}
              className="rounded-2xl border overflow-hidden backdrop-blur-md flex flex-col"
              style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,25,0.4)" }}
            >
              <div className="w-full overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
                <img src={t.photo} alt="" className="w-full h-full object-cover opacity-90" loading="lazy" />
              </div>
              <div className="p-5 flex flex-col gap-3 flex-1">
                <p className="text-lg italic" style={{ color: "#F2D589", fontFamily: "'Cormorant Garamond', serif" }}>
                  &ldquo;{t.title}&rdquo;
                </p>
                <p className="text-sm leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
                  {t.quote}
                </p>
                <p className="text-xs mt-auto" style={{ color: "rgba(245,239,227,0.55)" }}> {t.who}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-16 grid md:grid-cols-[1fr_1.15fr] gap-8 items-start" data-testid="soundbath-shape">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
            How it runs
          </p>
          <h2 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            An hour of listening, then the real part.
          </h2>
        </div>
        <div className="space-y-4 text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.8)" }}>
          <p>
            The soundbath itself runs 45&ndash;75 minutes, ending in silence.
            Then Dan stays — schmoozing, answering the questions people were too
            shy to ask in front of a crowd. That&apos;s where the evening stops
            being a performance and becomes a connection.
          </p>
          <p>
            Sound and piano requirements are simple, and Dan will walk your team
            through them. Travel is worked out per event.
          </p>
        </div>
      </section>

      <section className="mt-16 rounded-2xl border p-7 sm:p-10 backdrop-blur-md" style={{ borderColor: "rgba(232,196,138,0.25)", backgroundColor: "rgba(8,4,25,0.45)" }}>
        <h2 className="text-3xl sm:text-4xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Want to host a soundbath?
        </h2>
        <p className="text-base sm:text-lg leading-relaxed max-w-[58rem]" style={{ color: "rgba(245,239,227,0.82)" }}>
          Community organizer, program curator, retreat leader, or just
          someone with a living room and a group of friends, let&apos;s bring it to your space.
        </p>

        <p className="mt-6 text-[10px] uppercase tracking-[0.28em]" style={{ color: "#F2D589" }}>
          Piano soundbaths are perfect for
        </p>
        <ul className="mt-2 grid sm:grid-cols-2 gap-2 text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
          {PERFECT_FOR.map((p) => (
            <li key={p.label}>
              <span className="mr-2" aria-hidden="true">{p.icon}</span>
              {p.label}
            </li>
          ))}
        </ul>

        <div className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm leading-relaxed" style={{ color: "rgba(245,239,227,0.78)" }}>
          <p><span className="text-[#F2D589]">Offered:</span> in-person solo piano, or virtually via livestream / Zoom</p>
          <p><span className="text-[#F2D589]">Location:</span> Based in Las Vegas, NV, travel available on request</p>
          <p><span className="text-[#F2D589]">Duration:</span> 45&ndash;75 minutes (plus setup &amp; optional intro/debrief)</p>
          <p><span className="text-[#F2D589]">Equipment:</span> Digital or acoustic piano provided if needed</p>
        </div>

        <p className="mt-6 text-[10px] uppercase tracking-[0.28em]" style={{ color: "#F2D589" }}>
          Optional add-ons
        </p>
        <ul className="mt-2 space-y-1 text-sm leading-relaxed" style={{ color: "rgba(245,239,227,0.78)" }}>
          <li>&middot; Short talk on Jewish music, modes, and improvisation</li>
          <li>&middot; Movement / yoga / art integrations with collaborators</li>
          <li>&middot; Guided writing or reflection prompt after the session</li>
          <li>&middot; Custom thematic soundbaths (Elul, Tisha B&apos;Av, etc.)</li>
        </ul>

        <Link
          to="/contact"
          data-testid="soundbath-book-cta"
          className="mt-8 inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em]"
          style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", fontWeight: 600 }}
        >
          Book a soundbath &rsaquo;
        </Link>
      </section>

      <section className="mt-12 text-center text-sm" style={{ color: "rgba(245,239,227,0.62)" }}>
        Looking for something more interactive? &rarr;{" "}
        <Link to="/community-events" className="underline underline-offset-4 hover:text-[#F2D589]">
          interactive concert experiences
        </Link>
      </section>
    </MarketingShell>
  );
}
